import { useState, ReactNode, Suspense } from "react"
import { Head, Link, Router, useMutation, useRouter, useSession } from "blitz"
import { Transition } from "@tailwindui/react"
import {
  HiOutlineClipboardList,
  HiOutlineCurrencyDollar,
  HiUserCircle,
  HiUserGroup,
  HiCalendar,
} from "react-icons/hi"
import { RiCoupon2Fill } from "react-icons/ri"
import { AiOutlineIdcard } from "react-icons/ai"
import { BiLineChart } from "react-icons/bi"
import { MdFiberNew, MdCalculate, MdOutlineMenuBook } from "react-icons/md"
import { TiGroupOutline } from "react-icons/ti"
import { ImWrench } from "react-icons/im"
import clsx from "clsx"
import { IconType } from "react-icons/lib"
import { useCurrentUser } from "app/core/hooks/useCurrentUser"
import logout from "app/auth/mutations/logout"
import { fullName } from "app/orders/utils"
import HeaderLogo from "app/core/components/HeaderLogo"
import { ImpersonatingUserNotice } from "app/core/components/ImpersonatingUserNotice"
import { Role } from "db"
import toast, { Toaster } from "react-hot-toast"

Router.events.on("routeChangeComplete", () => {
  toast.dismiss()
})

type LayoutProps = {
  title?: string
  children: ReactNode
}

export const AppLayout = ({ title, children }: LayoutProps) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false)

  return (
    <>
      <Head>
        <title>{title || "Homeschool Boss"}</title>
      </Head>

      <ImpersonatingUserNotice />

      <div id="background" className="flex h-screen overflow-hidden bg-gray-100">
        {/* Off-canvas menu for mobile */}
        <div className="md:hidden">
          <div
            className="fixed inset-0 z-40 flex"
            style={{ pointerEvents: menuIsOpen ? "auto" : "none" }}
          >
            {/*
              Off-canvas menu overlay, show/hide based on off-canvas menu state.

              Entering: "transition-opacity ease-linear duration-300"
                From: "opacity-0"
                To: "opacity-100"
              Leaving: "transition-opacity ease-linear duration-300"
                From: "opacity-100"
                To: "opacity-0"
            */}
            <Transition
              show={menuIsOpen}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              {(ref) => (
                <div ref={ref} className="fixed inset-0">
                  <div className="absolute inset-0 bg-gray-600 opacity-75" />
                </div>
              )}
            </Transition>

            {/*
              Off-canvas menu, show/hide based on off-canvas menu state.

              Entering: "transition ease-in-out duration-300 transform"
                From: "-translate-x-full"
                To: "translate-x-0"
              Leaving: "transition ease-in-out duration-300 transform"
                From: "translate-x-0"
                To: "-translate-x-full"
            */}
            <Transition
              show={menuIsOpen}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              {(ref) => (
                <div
                  ref={ref}
                  className="relative flex flex-col flex-1 w-full max-w-xs bg-gray-800"
                >
                  <div className="absolute top-0 right-0 p-1 -mr-14">
                    {menuIsOpen && (
                      <button
                        className="flex items-center justify-center w-12 h-12 rounded-full focus:outline-none focus:bg-gray-600 z-50"
                        aria-label="Close sidebar"
                        onClick={() => setMenuIsOpen(false)}
                      >
                        <svg
                          className="w-6 h-6 text-white"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                  <Sidebar />
                </div>
              )}
            </Transition>
            <div className="flex-shrink-0 w-14">
              {/* Force sidebar to shrink to fit close icon */}
            </div>
          </div>
        </div>

        {/* Static sidebar for desktop */}
        <div id="sidebar" className="hidden md:flex md:flex-shrink-0">
          <div className="flex flex-col w-64">
            <Sidebar />
          </div>
        </div>

        <div className="flex flex-col flex-1 w-0 overflow-hidden">
          <div id="sidebar-menu-button" className="pt-1 pl-1 md:hidden sm:pl-3 sm:pt-3">
            <button
              className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:bg-gray-200 transition ease-in-out duration-150"
              aria-label="Open sidebar"
              onClick={() => setMenuIsOpen(true)}
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>
          <main className="relative z-0 flex-1 overflow-y-auto focus:outline-none">
            <div className="mx-auto space-y-4 max-w-7xl">{children}</div>
          </main>
        </div>
      </div>
      <Toaster
        position="top-right"
        toastOptions={{ success: { duration: 5 * 60000 }, error: { duration: 30 * 60000 } }}
      />
    </>
  )
}

type MenuItem = {
  text: string
  href: string
  icon: IconType
  roles?: Role[]
}

type MenuLinkProps = {
  menu: MenuItem
}

const MenuLink = ({ menu }: MenuLinkProps) => {
  const router = useRouter()
  const isActive = router.pathname.startsWith(menu.href)
  return (
    <Link href={menu.href} key={menu.text}>
      <a
        className={clsx(
          "flex items-center px-2 py-2 text-sm font-medium  group leading-5 rounded-md focus:outline-none focus:bg-gray-700 transition ease-in-out duration-150",
          isActive ? "text-white bg-gray-900" : "text-gray-300 hover:text-white hover:bg-gray-700"
        )}
      >
        <menu.icon
          className={clsx(
            "w-6 h-6 mr-3  group-hover:text-gray-300 group-focus:text-gray-300 transition ease-in-out duration-150",
            isActive ? "text-gray-300" : "text-gray-400"
          )}
        />
        {menu.text}
      </a>
    </Link>
  )
}

const menuItems: MenuItem[] = [
  { text: "MAP Testing", href: "/map-testing", icon: BiLineChart },
  // { text: "MAP Accelerator", href: "/map-accelerator", icon: MdCalculate },
  { text: "MAP Fluency", href: "/map-fluency", icon: MdOutlineMenuBook },
  { text: "Mindprint", href: "/mindprint", icon: MdFiberNew },
  { text: "ID Card Generator", href: "/id-generator", icon: AiOutlineIdcard },
  { text: "Reports", href: "/reports", icon: BiLineChart },
]

const UserInfo = () => {
  const router = useRouter()
  const currentUser = useCurrentUser()
  const [logoutMutation] = useMutation(logout)
  if (!currentUser) return null
  return (
    <div className="flex justify-between w-full ml-3">
      <Link href="/account">
        <a className="block">
          <div className="text-sm font-medium leading-5 text-white">
            <div>{fullName(currentUser.organization)}</div>
            <div className="text-xs text-gray-300">Settings</div>
          </div>
        </a>
      </Link>
      <button
        className="text-xs font-medium leading-4 text-gray-300 transition duration-150 ease-in-out group-hover:text-gray-200"
        onClick={async () => {
          await logoutMutation()
          await router.push("/login")
        }}
      >
        Logout
      </button>
    </div>
  )
}

const Sidebar = () => {
  return (
    <div className="flex flex-col flex-1 h-0 bg-gray-800">
      <div className="flex flex-col flex-1 pt-5 pb-4 overflow-y-auto">
        <HeaderLogo className="px-4" />
        <nav className="flex-1 px-2 mt-5 space-y-1 bg-gray-800">
          {menuItems.map((menu) => (
            <MenuLink menu={menu} key={menu.text} />
          ))}

          <AdminMenu />
        </nav>
      </div>
      <div className="flex flex-shrink-0 p-4 bg-gray-700">
        <div className="flex items-center w-full">
          <div>
            <HiUserCircle className="inline-block text-gray-400 rounded-full h-9 w-9" />
          </div>
          <Suspense fallback={<div />}>
            <UserInfo />
          </Suspense>
        </div>
      </div>
    </div>
  )
}

const adminMenuItems: MenuItem[] = [
  {
    text: "Proctor",
    href: "/admin/proctor",
    icon: HiOutlineClipboardList,
    roles: [Role.SUPERADMIN, Role.PROCTOR, Role.ADMIN],
  },
  {
    text: "Orders",
    href: "/admin/orders",
    icon: HiOutlineCurrencyDollar,
    roles: [Role.SUPERADMIN, Role.PROCTOR, Role.ADMIN],
  },
  {
    text: "Test Results",
    href: "/admin/test-results",
    icon: BiLineChart,
    roles: [Role.SUPERADMIN, Role.ADMIN],
  },
  {
    text: "Rosters",
    href: "/admin/rosters",
    icon: HiUserGroup,
    roles: [Role.SUPERADMIN, Role.ADMIN, Role.PROCTOR],
  },
  {
    text: "Schedule",
    href: "/admin/schedule",
    icon: HiCalendar,
    roles: [Role.SUPERADMIN, Role.ADMIN],
  },
  {
    text: "Discounts",
    href: "/admin/discounts",
    icon: RiCoupon2Fill,
    roles: [Role.SUPERADMIN, Role.ADMIN],
  },
  { text: "Tools", href: "/admin/tools", icon: ImWrench, roles: [Role.SUPERADMIN, Role.ADMIN] },
  {
    text: "Batch Registration",
    href: "/admin/batch-registration/1",
    icon: HiCalendar,
    roles: [Role.SUPERADMIN, Role.ADMIN],
  },
]

const AdminMenu = () => {
  const session = useSession({ suspense: false })

  const menuItems = adminMenuItems.filter((m) => {
    if (!m.roles) return false
    if (!session.roles) return false

    const [role] = session.roles

    return m.roles.includes(role)
  })

  if (menuItems.length === 0) return null

  return (
    <>
      <div className="px-2 pt-6">
        <div className="py-1 text-sm font-medium text-gray-400 border-b border-gray-600">Admin</div>
      </div>
      {menuItems.map((menu) => (
        <MenuLink menu={menu} key={menu.text} />
      ))}
    </>
  )
}
